import { crush } from '@tedconf/js-crushinator-helpers';
import cx from 'classnames';
import { formatDistanceToNowStrict } from 'date-fns';
import { has } from 'lodash-es';
import { motion } from 'motion/react';
import { type RefObject } from 'react';

import { Text } from 'components/@tedui';
import Image from 'components/image';
import { Link } from 'components/router';

import { ViewCount } from 'lib/business';
import getImageFromSet from 'lib/get-image-from-set';
import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';
import { SailthruFormattedItem } from 'lib/sailthru';
import { abbreviateNumber } from 'lib/util';

import { heroVariants, imageVariants } from '../animations';
import { IMAGE_WIDTH } from '../constants';

interface HeroTalkProps {
  heroTalk: SailthruFormattedItem;
  leftColumnRef: RefObject<HTMLDivElement | null>;
}

export function HeroTalk({ heroTalk, leftColumnRef }: HeroTalkProps) {
  const isMobileWidth = useIsBreakpointWidth({
    size: 'sm',
    breakPointType: 'tui'
  });

  const heroTalkDuration = Math.floor(heroTalk.duration / 60);
  const featuredImage = getImageFromSet(heroTalk?.primaryImageSet, '16x9');
  const heroTalkLink = `/talks/${heroTalk?.slug}`;

  return (
    <motion.div className="flex-1" variants={heroVariants} ref={leftColumnRef}>
      <Link href={heroTalkLink} isZenithLink>
        <motion.div
          className="relative mb-4 md-tui:mb-6"
          variants={imageVariants}
        >
          <div className="aspect-h-9 aspect-w-16 overflow-hidden md-tui:rounded-sm">
            <Image
              fill
              className="object-cover"
              src={crush(featuredImage || '', {
                width: IMAGE_WIDTH,
                quality: 80
              })}
              alt={heroTalk?.slug}
              sizes="@media (min-width: 768px) 90vw, 100vw"
            />
          </div>

          <Text
            variant="unset"
            UNSAFE_className="md:invisible sm:visible text-tui-xs text-white absolute bottom-2 right-2 font-bold bg-textSecondary-onLight px-1 rounded-xs uppercase leading-[20.7px] -tracking-[0.72px]"
            tag="p"
          >
            {heroTalkDuration} min
          </Text>
        </motion.div>
      </Link>

      <motion.div
        className="flex flex-col gap-5 px-4 pb-6 text-center md-tui:px-0 md-tui:pb-5"
        variants={heroVariants}
      >
        <Link href={heroTalkLink} isZenithLink>
          <Text
            tag="p"
            variant="header2"
            useNewTextStyles
            weight="font-semibold"
          >
            {heroTalk?.title}
          </Text>
        </Link>

        <Text variant="body1" useNewTextStyles tag="p">
          {heroTalk?.description}
        </Text>

        <div
          className={cx('flex justify-center', {
            flex: isMobileWidth
          })}
        >
          <Text tag="p" variant="caption1" useNewTextStyles>
            {has(heroTalk, 'viewedCount') && (
              <ViewCount count={heroTalk.viewedCount as number}>
                {`${abbreviateNumber(heroTalk.viewedCount as number)}`} plays
                ·{' '}
              </ViewCount>
            )}
            <>{heroTalk?.presenterDisplayName} · </>

            {has(heroTalk, 'publishedAt') && (
              <>
                {formatDistanceToNowStrict(new Date(heroTalk!.publishedAt))} ago
              </>
            )}
          </Text>
        </div>
      </motion.div>
    </motion.div>
  );
}
